import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Search, MapPin, Filter, Building2 } from "lucide-react";

const TIER_LABEL = { top_spot: "Elite", featured: "Pro", basic: "Basic" };
const TIER_PILL = {
    top_spot: "bg-amber-200/40 text-amber-900 border border-amber-500/40",
    featured: "bg-primary/10 text-primary border border-primary/30",
    basic: "bg-muted text-foreground/70",
};

function AttorneyRow({ a }) {
    return (
        <Link to={`/attorney/${a.id}`} className="ll-card ll-card-hover block" data-testid={`directory-attorney-${a.id}`}>
            <div className="flex items-start gap-3">
                {a.photo_url ? (
                    <img src={a.photo_url} alt={a.name} className="w-14 h-14 rounded-xl object-cover border border-border flex-shrink-0" />
                ) : (
                    <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center border border-border flex-shrink-0 font-serif text-lg text-muted-foreground">
                        {a.name?.[0] || "?"}
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <h3 className="font-serif text-lg font-semibold tracking-tight truncate">{a.name}</h3>
                            <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                <Building2 className="h-3.5 w-3.5" /> {a.firm_name || "Independent practice"}
                                {(a.service_area_city || a.service_area_state) && (
                                    <>
                                        <span className="mx-1">·</span>
                                        <MapPin className="h-3.5 w-3.5" />
                                        {[a.service_area_city, a.service_area_state].filter(Boolean).join(", ")}
                                    </>
                                )}
                            </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${TIER_PILL[a.tier]}`}
                              title="Profile tier — set by the attorney's plan, not a client rating">
                            {TIER_LABEL[a.tier]}
                        </span>
                    </div>
                    {a.bio && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.bio}</p>}
                    {a.rating_count > 0 && (
                        <div className="text-xs mt-2 text-amber-600">
                            ★ {a.rating_avg?.toFixed(1)} <span className="text-muted-foreground">({a.rating_count} client review{a.rating_count === 1 ? "" : "s"})</span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

/**
 * FindAttorneyPage — public directory at /find-an-attorney.
 * Also reused by LocationDirectoryPage which pre-fills filters from URL slugs.
 */
export default function FindAttorneyPage({ initialState, initialCounty, initialCategory, hero }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState({ states: [], counties: [], categories: [] });
    const [stateSlug, setStateSlug] = useState(initialState || searchParams.get("state") || "");
    const [countySlug, setCountySlug] = useState(initialCounty || searchParams.get("county") || "");
    const [categorySlug, setCategorySlug] = useState(initialCategory || searchParams.get("category_slug") || "");
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [data, setData] = useState({ results: [], total: 0, page: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try { const r = await api.get("/find-attorneys/filters"); setFilters(r.data); }
            catch { /* ignore */ }
        })();
    }, []);

    const countiesInState = useMemo(() => {
        const stateObj = filters.states.find((s) => s.slug === stateSlug);
        if (!stateObj) return [];
        return filters.counties.filter((c) => c.state_code === stateObj.code)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [filters, stateSlug]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError("");
            try {
                const params = new URLSearchParams();
                if (stateSlug) params.set("state", stateSlug);
                if (countySlug) params.set("county", countySlug);
                if (categorySlug) params.set("category_slug", categorySlug);
                if (q) params.set("q", q);
                params.set("limit", "30");
                const r = await api.get(`/find-attorneys?${params.toString()}`);
                if (cancelled) return;
                setData(r.data);
                // Mirror filters to URL so it's deep-linkable from the directory page.
                if (!initialState) setSearchParams(params, { replace: true });
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.detail || "Couldn't load directory.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [stateSlug, countySlug, categorySlug, q]); // eslint-disable-line

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="find-attorney-page">
            {hero || (
                <header className="mb-8 text-center">
                    <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">Find an attorney</h1>
                    <p className="text-base text-muted-foreground mt-3 max-w-2xl mx-auto">Search by state, county, or practice area. Listings are ranked by location match, practice area, profile tier, and client rating.</p>
                </header>
            )}

            <div className="ll-card mb-6" data-testid="directory-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-medium text-muted-foreground">
                    <Filter className="h-4 w-4" /> Filters
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <select className="ll-input" value={stateSlug} onChange={(e) => { setStateSlug(e.target.value); setCountySlug(""); }} data-testid="filter-state">
                        <option value="">All states</option>
                        {filters.states.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    </select>
                    <select className="ll-input" value={countySlug} onChange={(e) => setCountySlug(e.target.value)} disabled={!stateSlug} data-testid="filter-county">
                        <option value="">All counties</option>
                        {countiesInState.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                    <select className="ll-input" value={categorySlug} onChange={(e) => setCategorySlug(e.target.value)} data-testid="filter-category">
                        <option value="">All practice areas</option>
                        {filters.categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input className="ll-input pl-9" placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-q" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16" data-testid="directory-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : error ? (
                <div className="ll-card text-sm text-destructive" data-testid="directory-error">{error}</div>
            ) : data.results.length === 0 ? (
                <div className="ll-card text-sm text-muted-foreground text-center py-12" data-testid="directory-empty">
                    No attorneys match these filters yet. Try widening your search or removing a filter.
                </div>
            ) : (
                <>
                    <div className="text-xs text-muted-foreground mb-3" data-testid="directory-count">{data.total} attorney{data.total === 1 ? "" : "s"} found</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="directory-results">
                        {data.results.map((a) => <AttorneyRow key={a.id} a={a} />)}
                    </div>
                </>
            )}
        </div>
    );
}
